import { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import type { CursorPage } from '@dru/types';
import { Alert, Button, Card, CardHeader, EmptyState, ErrorState, Input, StatusBadge, TextSkeleton, cn } from '@dru/ui';
import { Dialog } from '../../../components/Dialog';
import { api } from '../../../lib/api';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { groupTests } from '../../../lib/testGroups';

/**
 * A company's packages (owner's decision, 29 Oct 2026).
 *
 * The two platform defaults — pre-employment and annual — apply to every
 * company on their own; nothing has to be configured. Here a test can be
 * added to or removed from either for this company only, and, rarely, a
 * package made for this company alone, which its HR then picks when
 * arranging an examination.
 */

interface TestBrief { id: string; code: string; name: string; category: string }
interface CompanyPackage {
  id: string; code: string; name: string; description: string | null; version: number;
  defaultFor: 'PRE_EMPLOYMENT' | 'YEARLY' | null; own: boolean; isActive: boolean;
  tests: Array<TestBrief & { added: boolean }>;
  removed: TestBrief[];
}

const USED_FOR: Record<string, string> = {
  PRE_EMPLOYMENT: 'Used for every pre-employment examination',
  YEARLY: 'Used for annual, periodic and every other examination',
};

export function CompanyPackages({ tenantId }: { tenantId: string }) {
  const base = `/admin/tenants/${tenantId}/setup/packages`;
  const list = useQuery<{ items: CompanyPackage[] }>(() => api.get(base), [tenantId]);
  const [changing, setChanging] = useState<CompanyPackage | null>(null);
  const [editing, setEditing] = useState<CompanyPackage | 'new' | null>(null);

  if (list.error) return <ErrorState title="Could not load the packages" detail={list.error} onRetry={list.reload} />;
  const items = list.data?.items ?? [];
  const defaults = items.filter((p) => p.defaultFor);
  const own = items.filter((p) => p.own);

  return (
    <Card>
      <CardHeader
        title="Packages"
        hint="Pre-employment and annual packages apply to this company automatically. Add or remove a test for this company only, or make a package just for it."
        actions={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" aria-hidden /> New company package</Button>}
      />
      <div className="space-y-4 p-4 pt-0">
        {list.loading && !list.data ? <TextSkeleton lines={4} /> : (
          <>
            <div className="grid gap-3 lg:grid-cols-2">
              {defaults.map((p) => <PackageCard key={p.id} pkg={p} onChange={() => setChanging(p)} />)}
            </div>
            {defaults.length === 0 && (
              <Alert>The default packages are missing from the catalogue. Examinations cannot be raised until they exist.</Alert>
            )}
            <div>
              <h3 className="mb-2 text-caption font-semibold uppercase tracking-wide text-neutral-600">This company’s own packages</h3>
              {own.length ? (
                <div className="grid gap-3 lg:grid-cols-2">
                  {own.map((p) => <PackageCard key={p.id} pkg={p} onChange={() => setEditing(p)} />)}
                </div>
              ) : (
                <p className="text-small text-neutral-500">None. Make one only when this company needs a different set of tests altogether.</p>
              )}
            </div>
          </>
        )}
      </div>

      {changing && (
        <ChangeTestsDialog
          pkg={changing} path={`${base}/${changing.id}/changes`}
          onClose={() => setChanging(null)} onSaved={() => { setChanging(null); list.reload(); }}
        />
      )}
      {editing && (
        <OwnPackageDialog
          pkg={editing === 'new' ? null : editing} base={base}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }}
        />
      )}
    </Card>
  );
}

function PackageCard({ pkg, onChange }: { pkg: CompanyPackage; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const added = pkg.tests.filter((t) => t.added).length;
  const groups = groupTests(pkg.tests);
  return (
    <div className={cn('rounded-card border border-neutral-200 p-4', !pkg.isActive && 'opacity-60')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-neutral-900">{pkg.name}</span>
            {pkg.defaultFor
              ? <StatusBadge tone="covered">Default</StatusBadge>
              : <StatusBadge tone={pkg.isActive ? 'neutral' : 'pending'}>{pkg.isActive ? 'Company package' : 'Retired'}</StatusBadge>}
          </div>
          <div className="mt-0.5 text-caption text-neutral-500">
            {pkg.defaultFor ? USED_FOR[pkg.defaultFor] : 'HR picks it when arranging an examination'}
          </div>
          <div className="mt-1 text-small text-neutral-700">
            {pkg.tests.length} test{pkg.tests.length === 1 ? '' : 's'}
            {added > 0 && <span className="text-primary-700"> · {added} added for this company</span>}
            {pkg.removed.length > 0 && <span className="text-warning-700"> · {pkg.removed.length} removed</span>}
          </div>
        </div>
        <Button variant="secondary" size="sm" className="shrink-0" onClick={onChange}>
          {pkg.defaultFor ? 'Change tests' : 'Edit'}
        </Button>
      </div>
      {/* The groups first — what the package covers at a glance — and the
          tests themselves one tap away. */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {groups.map((g) => (
          <span key={g.key} className={cn(
            'rounded-badge px-2 py-0.5 text-caption',
            g.tests.some((t) => t.added) ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-700',
          )}>
            {g.label} · {g.tests.length}
          </span>
        ))}
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="min-h-8 px-1 text-caption font-medium text-primary-600">
          {open ? 'Hide tests' : 'Show tests'}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-2 border-t border-neutral-100 pt-3">
          {groups.map((g) => (
            <div key={g.key} className="text-small">
              <div className="font-medium text-neutral-800">{g.label}</div>
              <div className="text-neutral-600">
                {g.tests.map((t, i) => (
                  <span key={t.id}>
                    {i > 0 && ', '}
                    <span className={t.added ? 'font-medium text-primary-700' : undefined}>{t.added && '+ '}{t.name}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {pkg.removed.length > 0 && (
        <div className="mt-2 text-small text-warning-700">
          Removed for this company: {pkg.removed.map((t) => t.name).join(', ')}
        </div>
      )}
    </div>
  );
}

/** Every live test in the catalogue, for the pickers. */
function useCatalogueTests() {
  return useQuery<TestBrief[]>(
    () => fetchAll<TestBrief & { isActive?: boolean }>((cursor) => api.get<CursorPage<TestBrief & { isActive?: boolean }>>(
      `/tests?brief=true&limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )).then((rows) => rows.filter((t) => t.isActive !== false)),
    [],
  );
}

/** Search the catalogue and pick a test not already chosen. */
function TestSearch({ all, exclude, onPick }: { all: TestBrief[]; exclude: Set<string>; onPick: (t: TestBrief) => void }) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    return all.filter((t) => !exclude.has(t.id)
      && (t.name.toLowerCase().includes(term) || t.code.toLowerCase().includes(term))).slice(0, 8);
  }, [q, all, exclude]);
  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tests to add, e.g. Audiometry" className="h-10 pl-9" aria-label="Search tests to add" />
      </div>
      {hits.length > 0 && (
        <ul className="mt-1 max-h-56 overflow-y-auto rounded-input border border-neutral-200">
          {hits.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => { onPick(t); setQ(''); }}
                className="flex min-h-10 w-full items-center justify-between gap-3 px-3 py-2 text-left text-small hover:bg-neutral-50">
                <span className="min-w-0"><span className="text-neutral-900">{t.name}</span> <span className="text-caption text-neutral-500">{t.code}</span></span>
                <Plus className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {q.trim().length >= 2 && hits.length === 0 && <p className="mt-1 text-caption text-neutral-500">No test matches “{q.trim()}”.</p>}
    </div>
  );
}

function ChangeTestsDialog({ pkg, path, onClose, onSaved }: {
  pkg: CompanyPackage; path: string; onClose: () => void; onSaved: () => void;
}) {
  const all = useCatalogueTests();
  // The package's own tests, whether currently in or removed for the company.
  const own = useMemo(() => [...pkg.tests.filter((t) => !t.added), ...pkg.removed.map((t) => ({ ...t, added: false }))]
    .sort((a, b) => a.name.localeCompare(b.name)), [pkg]);
  const [removed, setRemoved] = useState<Set<string>>(() => new Set(pkg.removed.map((t) => t.id)));
  const [added, setAdded] = useState<TestBrief[]>(() => pkg.tests.filter((t) => t.added));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const exclude = useMemo(() => new Set([...own.map((t) => t.id), ...added.map((t) => t.id)]), [own, added]);

  const toggle = (id: string) => setRemoved((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function save() {
    setBusy(true); setError(null);
    try {
      await api.patch(path, { add: added.map((t) => t.id), remove: [...removed] });
      onSaved();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog
      title={`${pkg.name} — this company`}
      info="Changes apply to this company only. The default package itself stays as it is for everyone else."
      onClose={onClose} wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} onClick={() => void save()}>Save</Button>
        </>
      )}
    >
      <div className="space-y-4">
        <section>
          <h3 className="mb-1 text-small font-semibold text-neutral-800">Add tests</h3>
          {all.loading && !all.data ? <TextSkeleton lines={1} /> : <TestSearch all={all.data ?? []} exclude={exclude} onPick={(t) => setAdded((a) => [...a, t])} />}
          {added.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {added.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 rounded-badge bg-primary-50 py-0.5 pl-2 pr-1 text-caption text-primary-700">
                  + {t.name}
                  <button type="button" aria-label={`Do not add ${t.name}`} onClick={() => setAdded((a) => a.filter((x) => x.id !== t.id))}
                    className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-primary-100">
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>
        <section>
          <h3 className="mb-1 text-small font-semibold text-neutral-800">
            Tests in the package <span className="font-normal text-neutral-500">· untick to remove for this company</span>
          </h3>
          <div className="max-h-96 space-y-3 overflow-y-auto rounded-input border border-neutral-200 p-3">
            {groupTests(own).map((g) => (
              <div key={g.key}>
                <div className="mb-0.5 text-caption font-semibold uppercase tracking-wide text-neutral-600">{g.label}</div>
                <div className="grid gap-x-4 sm:grid-cols-2">
                  {g.tests.map((t) => (
                    <label key={t.id} className="flex min-h-10 cursor-pointer items-center gap-2 rounded px-1 text-small hover:bg-neutral-50">
                      <input type="checkbox" className="h-4 w-4" checked={!removed.has(t.id)} onChange={() => toggle(t.id)} />
                      <span className={cn('min-w-0', removed.has(t.id) && 'text-neutral-400 line-through')}>{t.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}

function OwnPackageDialog({ pkg, base, onClose, onSaved }: {
  pkg: CompanyPackage | null; base: string; onClose: () => void; onSaved: () => void;
}) {
  const all = useCatalogueTests();
  const [name, setName] = useState(pkg?.name ?? '');
  const [chosen, setChosen] = useState<TestBrief[]>(() => pkg?.tests ?? []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exclude = useMemo(() => new Set(chosen.map((t) => t.id)), [chosen]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); onSaved(); } catch (e) { setError(errorMessage(e)); setBusy(null); }
  }
  const body = { name: name.trim(), testIds: chosen.map((t) => t.id) };
  const save = () => act('save', () => (pkg
    ? api.patch(`${base}/${pkg.id}`, body)
    : api.post(base, body, crypto.randomUUID())));

  return (
    <Dialog
      title={pkg ? `Edit ${pkg.name}` : 'New company package'}
      info="A package for this company alone. Its HR picks it when arranging an examination; the defaults still apply otherwise."
      onClose={onClose} wide
      footer={(
        <>
          {pkg && (
            <Button variant="secondary" className="mr-auto" loading={busy === 'retire'}
              onClick={() => void act('retire', () => api.patch(`${base}/${pkg.id}`, { isActive: !pkg.isActive }))}>
              {pkg.isActive ? 'Retire' : 'Restore'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy === 'save'} disabled={name.trim().length < 2 || chosen.length === 0} onClick={() => void save()}>
            {pkg ? 'Save' : 'Create package'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-small font-medium text-neutral-800">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Canteen staff check" className="mt-1 h-10" />
        </label>
        <section>
          <h3 className="mb-1 text-small font-semibold text-neutral-800">Tests <span className="font-normal text-neutral-500">· {chosen.length} chosen</span></h3>
          {all.loading && !all.data ? <TextSkeleton lines={1} /> : <TestSearch all={all.data ?? []} exclude={exclude} onPick={(t) => setChosen((c) => [...c, t])} />}
          {chosen.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {chosen.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 rounded-badge bg-neutral-100 py-0.5 pl-2 pr-1 text-caption text-neutral-800">
                  {t.name}
                  <button type="button" aria-label={`Remove ${t.name}`} onClick={() => setChosen((c) => c.filter((x) => x.id !== t.id))}
                    className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-neutral-200">
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <EmptyState title="No tests yet" hint="Search above and add the tests this package should run." />
          )}
        </section>
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}
